import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

/**
 * Theme handling: dark, light, or follow the operating system.
 *
 * The chosen theme is persisted under `tges-csa.theme` and applied to
 * <html data-theme>. An inline script in index.html applies it before first
 * paint; this provider takes over afterwards and keeps them in sync.
 */

export const THEME_STORAGE_KEY = 'tges-csa.theme';

export type ThemePreference = 'dark' | 'light' | 'system';
export type ResolvedTheme = 'dark' | 'light';

const LIGHT_QUERY = '(prefers-color-scheme: light)';

export function readStoredPreference(): ThemePreference {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'dark';
  } catch {
    // localStorage throws in private browsing modes and inside sandboxed
    // iframes. Dark is the product default, so fall back to it silently.
    return 'dark';
  }
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference !== 'system') return preference;
  return window.matchMedia(LIGHT_QUERY).matches ? 'light' : 'dark';
}

/**
 * Writes the theme to the document. `data-theme-switching` suppresses colour
 * transitions for one frame, otherwise every element animates independently
 * and the change reads as a smear rather than a switch.
 */
export function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.setAttribute('data-theme-switching', '');
  root.setAttribute('data-theme', resolved);

  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute('content', resolved === 'light' ? '#ffffff' : '#0b0b0d');

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => root.removeAttribute('data-theme-switching'));
  });
}

interface ThemeContextValue {
  readonly preference: ThemePreference;
  readonly resolved: ResolvedTheme;
  readonly setPreference: (next: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }): ReactElement {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readStoredPreference());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(preference));

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Non-fatal: the theme still applies for this session.
    }
    const nextResolved = resolveTheme(next);
    setResolved(nextResolved);
    applyTheme(nextResolved);
  }, []);

  // Follow the OS while the preference is 'system'.
  useEffect(() => {
    if (preference !== 'system') return;
    const media = window.matchMedia(LIGHT_QUERY);
    const onChange = (): void => {
      const next: ResolvedTheme = media.matches ? 'light' : 'dark';
      setResolved(next);
      applyTheme(next);
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [preference]);

  // Mirror changes made in another tab.
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== THEME_STORAGE_KEY) return;
      const next = readStoredPreference();
      setPreferenceState(next);
      const nextResolved = resolveTheme(next);
      setResolved(nextResolved);
      applyTheme(nextResolved);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Reconciles with whatever the pre-paint script in index.html decided, and
  // with a change made in another tab. applyTheme is idempotent, so running it
  // whenever `resolved` changes is both correct and cheaper to reason about
  // than a mount-only effect that has to be kept in step by hand.
  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const value = useMemo(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside a ThemeProvider');
  return ctx;
}

/**
 * The script that runs before first paint, inlined into index.html. Applying
 * the theme here rather than in React avoids a flash of the wrong palette,
 * which is especially jarring going from dark to light.
 */
export const THEME_BOOTSTRAP_SCRIPT = `!function(){try{var k="${THEME_STORAGE_KEY}",t=window.localStorage.getItem(k),e="light"===t||"dark"===t||"system"===t?t:"dark",a="system"===e?window.matchMedia("${LIGHT_QUERY}").matches?"light":"dark":e;document.documentElement.setAttribute("data-theme",a);var r=document.querySelector('meta[name="theme-color"]');r&&r.setAttribute("content","light"===a?"#ffffff":"#0b0b0d")}catch(t){}}()`;
